import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserRole } from '../hooks/useUserRole';
import { tomApi, organizationApi, openPdfExport } from '../services/api';
import {
  TomMeasure,
  TomCategory,
  TomStatus,
  TomEffectiveness,
  Organization
} from '@dsgvo-buddy/types';
import {
  Title,
  Button,
  Table,
  TableColumn,
  TableRow,
  TableCell,
  Input,
  Select,
  Option,
  FlexBox,
  Text,
  Icon,
  ObjectStatus,
  BusyIndicator,
  Card,
  CardHeader
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/AllIcons.js';
import PageIntro from '../components/PageIntro';
import { pageIntros } from '../helpers/helpTexts';

const categoryConfig: Record<TomCategory, { label: string }> = {
  [TomCategory.ACCESS_CONTROL]: { label: 'Zugriffskontrolle' },
  [TomCategory.TRANSFER_CONTROL]: { label: 'Weitergabekontrolle' },
  [TomCategory.INPUT_CONTROL]: { label: 'Eingabekontrolle' },
  [TomCategory.AVAILABILITY]: { label: 'Verfügbarkeit' },
  [TomCategory.RESILIENCE]: { label: 'Belastbarkeit' },
  [TomCategory.RECOVERY]: { label: 'Wiederherstellung' },
  [TomCategory.SEPARATION]: { label: 'Trennbarkeit' },
  [TomCategory.DELETION]: { label: 'Löschung' },
  [TomCategory.PRIVACY_BY_DESIGN]: { label: 'Privacy by Design' },
  [TomCategory.ENCRYPTION]: { label: 'Verschlüsselung' },
  [TomCategory.PSEUDONYMIZATION]: { label: 'Pseudonymisierung' }
};

const statusConfig: Record<TomStatus, { label: string; state: string }> = {
  [TomStatus.PLANNED]: { label: 'Geplant', state: 'Information' },
  [TomStatus.IMPLEMENTED]: { label: 'Umgesetzt', state: 'Success' },
  [TomStatus.OUTDATED]: { label: 'Veraltet', state: 'Error' },
  [TomStatus.COMPENSATING]: { label: 'Kompensierend', state: 'Warning' }
};

const effectivenessConfig: Record<TomEffectiveness, { label: string; state: string }> = {
  [TomEffectiveness.HIGH]: { label: 'Hoch', state: 'Success' },
  [TomEffectiveness.MEDIUM]: { label: 'Mittel', state: 'Warning' },
  [TomEffectiveness.LOW]: { label: 'Niedrig', state: 'Error' },
  [TomEffectiveness.NOT_ASSESSED]: { label: 'Nicht bewertet', state: 'None' }
};

export default function TomListPage() {
  const navigate = useNavigate();
  const { canCreate } = useUserRole();
  const [measures, setMeasures] = useState<TomMeasure[]>([]);
  const [filteredMeasures, setFilteredMeasures] = useState<TomMeasure[]>([]);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<TomCategory | ''>('');
  const [statusFilter, setStatusFilter] = useState<TomStatus | ''>('');

  useEffect(() => { loadOrganizations(); }, []);
  useEffect(() => { if (currentOrg) { loadMeasures(); } }, [currentOrg]);
  useEffect(() => { applyFilters(); }, [measures, searchTerm, categoryFilter, statusFilter]);

  const loadOrganizations = async () => {
    const response = await organizationApi.getAll();
    if (response.success && response.data) {
      if (response.data.length > 0) setCurrentOrg(response.data[0]);
    }
    setLoading(false);
  };

  const loadMeasures = async () => {
    if (!currentOrg) return;
    setLoading(true);
    const response = await tomApi.getByOrganization(currentOrg.id);
    if (response.success && response.data) setMeasures(response.data);
    setLoading(false);
  };

  const applyFilters = () => {
    let filtered = [...measures];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(m =>
        m.name.toLowerCase().includes(term) ||
        m.description?.toLowerCase().includes(term) ||
        m.responsible_person?.toLowerCase().includes(term)
      );
    }
    if (categoryFilter) filtered = filtered.filter(m => m.category === categoryFilter);
    if (statusFilter) filtered = filtered.filter(m => m.status === statusFilter);
    setFilteredMeasures(filtered);
  };

  if (loading && !currentOrg) {
    return (
      <FlexBox alignItems="Center" justifyContent="Center" style={{ height: '100%' }}>
        <BusyIndicator active size="Large" />
      </FlexBox>
    );
  }

  return (
    <div className="page-layout">
      {/* Page Header */}
      <FlexBox justifyContent="SpaceBetween" alignItems="Center" style={{ marginBottom: '1.5rem' }}>
        <div>
          <Title level="H3" style={{ marginBottom: '0.25rem' }}>Technische und Organisatorische Maßnahmen</Title>
          <Text style={{ color: 'var(--sapNeutralTextColor)' }}>
            Sicherheitsmaßnahmen gemäß Art. 32 DSGVO
          </Text>
        </div>
        <FlexBox style={{ gap: '0.5rem' }}>
          <Button icon="pdf-attachment" design="Default" onClick={() => currentOrg && openPdfExport('tom', currentOrg.id)}>PDF Export</Button>
          {canCreate && (
            <Button icon="add" design="Emphasized" onClick={() => navigate('/tom/new')}>
              Neue Maßnahme
            </Button>
          )}
        </FlexBox>
      </FlexBox>

      <PageIntro {...pageIntros.tom} storageKey="tom" />

      {/* Filter Bar */}
      <FlexBox
        alignItems="End"
        wrap="Wrap"
        style={{
          gap: '0.75rem',
          marginBottom: '1rem',
          padding: '1rem',
          background: 'var(--sapObjectHeader_Background, var(--sapBaseColor))',
          borderRadius: '0.5rem',
          border: '1px solid var(--sapGroup_TitleBorderColor, #d9d9d9)'
        }}
      >
        <div style={{ flex: '1 1 300px', minWidth: '200px' }}>
          <Text style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.25rem' }}>Suche</Text>
          <Input
            placeholder="Name, Beschreibung, Verantwortlicher..."
            icon={<Icon name="search" />}
            value={searchTerm}
            onInput={(e: any) => setSearchTerm(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>

        <div style={{ flex: '0 0 220px' }}>
          <Text style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.25rem' }}>Kategorie</Text>
          <Select onChange={(e: any) => setCategoryFilter(e.detail.selectedOption.value)} style={{ width: '100%' }}>
            <Option value="">Alle Kategorien</Option>
            {Object.entries(categoryConfig).map(([key, cfg]) => (
              <Option key={key} value={key}>{cfg.label}</Option>
            ))}
          </Select>
        </div>

        <div style={{ flex: '0 0 180px' }}>
          <Text style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.25rem' }}>Status</Text>
          <Select onChange={(e: any) => setStatusFilter(e.detail.selectedOption.value)} style={{ width: '100%' }}>
            <Option value="">Alle Status</Option>
            {Object.entries(statusConfig).map(([key, cfg]) => (
              <Option key={key} value={key}>{cfg.label}</Option>
            ))}
          </Select>
        </div>

        {(searchTerm || categoryFilter || statusFilter) && (
          <Button
            icon="decline"
            design="Transparent"
            onClick={() => { setSearchTerm(''); setCategoryFilter(''); setStatusFilter(''); }}
            tooltip="Filter zurücksetzen"
          />
        )}
      </FlexBox>

      {/* Table Card */}
      <Card style={{ overflow: 'hidden' }}
        header={
          <CardHeader
            titleText={`${filteredMeasures.length} von ${measures.length} Maßnahmen`}
            subtitleText="Klicken Sie auf eine Zeile für Details"
            avatar={<Icon name="shield" />}
          />
        }
      >
        {loading ? (
          <FlexBox alignItems="Center" justifyContent="Center" style={{ padding: '3rem' }}>
            <BusyIndicator active size="Large" />
          </FlexBox>
        ) : filteredMeasures.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <Icon name="shield" style={{ fontSize: '3rem', color: 'var(--sapNeutralColor)', display: 'block', marginBottom: '1rem' }} />
            <Title level="H5" style={{ marginBottom: '0.5rem' }}>
              {measures.length === 0 ? 'Noch keine Maßnahmen' : 'Keine Treffer'}
            </Title>
            <Text style={{ display: 'block', color: 'var(--sapNeutralTextColor)', marginBottom: '1rem' }}>
              {measures.length === 0
                ? 'Erfassen Sie Ihre erste TOM-Maßnahme.'
                : 'Passen Sie Ihre Filterkriterien an.'}
            </Text>
            {measures.length === 0 && canCreate && (
              <Button icon="add" design="Emphasized" onClick={() => navigate('/tom/new')}>
                Neue Maßnahme erstellen
              </Button>
            )}
          </div>
        ) : (
          <div style={{ padding: '0 1rem 1rem' }}>
            <Table
              style={{ width: '100%' }}
              onRowClick={(e: any) => {
                const index = parseInt(e.detail.row.dataset.index);
                navigate(`/tom/${filteredMeasures[index].id}`);
              }}
              columns={
                <>
                  <TableColumn><Text style={{ fontWeight: '600' }}>Maßnahme</Text></TableColumn>
                  <TableColumn><Text style={{ fontWeight: '600' }}>Kategorie</Text></TableColumn>
                  <TableColumn><Text style={{ fontWeight: '600' }}>Status</Text></TableColumn>
                  <TableColumn demandPopin minWidth={500}><Text style={{ fontWeight: '600' }}>Wirksamkeit</Text></TableColumn>
                  <TableColumn demandPopin minWidth={600}><Text style={{ fontWeight: '600' }}>Verantwortlich</Text></TableColumn>
                  <TableColumn demandPopin minWidth={700}><Text style={{ fontWeight: '600' }}>Prüfdatum</Text></TableColumn>
                </>
              }
            >
              {filteredMeasures.map((measure, index) => (
                <TableRow key={measure.id} data-index={index} type="Active">
                  <TableCell>
                    <div>
                      <Text style={{ fontWeight: '600' }}>{measure.name}</Text>
                      {measure.description && (
                        <Text style={{ fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)', display: 'block', marginTop: '0.125rem' }}>
                          {measure.description.length > 80
                            ? measure.description.substring(0, 80) + '...'
                            : measure.description}
                        </Text>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Text>{categoryConfig[measure.category]?.label ?? measure.category}</Text>
                  </TableCell>
                  <TableCell>
                    <ObjectStatus state={statusConfig[measure.status]?.state as any} showDefaultIcon>
                      {statusConfig[measure.status]?.label ?? measure.status}
                    </ObjectStatus>
                  </TableCell>
                  <TableCell>
                    <ObjectStatus state={effectivenessConfig[measure.effectiveness]?.state as any}>
                      {effectivenessConfig[measure.effectiveness]?.label ?? measure.effectiveness}
                    </ObjectStatus>
                  </TableCell>
                  <TableCell>
                    <Text>{measure.responsible_person ?? '-'}</Text>
                  </TableCell>
                  <TableCell>
                    <Text>
                      {measure.review_date
                        ? new Date(measure.review_date).toLocaleDateString('de-DE')
                        : '-'}
                    </Text>
                  </TableCell>
                </TableRow>
              ))}
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
